/* eslint-disable no-restricted-globals, no-unused-vars */
// ─────────────────────────────────────────────────────────────────────────
// SERGA // PERSONAL OS — boot + wiring
// Exposed as boot() so the React shell can start it after the HUD DOM mounts
// and tear it down on unmount (renderer, loop, listeners, timers).
// ─────────────────────────────────────────────────────────────────────────
import { createWorld } from './world.js';
import { createHUD, loadSettings } from './hud.js';
import { createTray } from './tray.js';
import { createSonic } from './sonic.js';
import { loadDraft } from './store.js';
import { createShell } from './console.js';

// accent → CSS vars
const ACCENT_CSS = {
  teal:   { a: '#2fe6d4', b: '#14c4d6', dim: '#0d6d72', rgb: '47, 230, 212' },
  cyan:   { a: '#49b6ff', b: '#2f7fe6', dim: '#16557f', rgb: '73, 182, 255' },
  amber:  { a: '#ffb454', b: '#ff8a3d', dim: '#8a5a1e', rgb: '255, 180, 84' },
  violet: { a: '#b07cff', b: '#7c5cff', dim: '#4a3a8a', rgb: '176, 124, 255' },
};
function applyAccentCSS(name) {
  const c = ACCENT_CSS[name] || ACCENT_CSS.teal;
  const r = document.documentElement.style;
  r.setProperty('--accent', c.a); r.setProperty('--accent-2', c.b);
  r.setProperty('--accent-dim', c.dim); r.setProperty('--accent-rgb', c.rgb);
}

export function boot(opts = {}) {
  const sessionLabel = opts.sessionLabel || 'serge.ba';
  const projectCount = opts.projectCount != null ? opts.projectCount : 5;
  const authed = !!opts.authed;
  // rehydrate mock drafts (finance/travel/zones) before the model is built
  loadDraft();
  const settings = loadSettings();

  // scanlines
  const fx = document.getElementById('fx');
  const applyScan = on => fx.classList.toggle('scan', !!on);
  applyScan(settings.scan);

  applyAccentCSS(settings.accent);

  // ── CLOUD portal — ZN-04 blooms the home-node landing open in-place ─────────
  // Loading model: PREFETCH the bundle bytes on idle (no GPU/memory), BUILD the
  // scene lazily on enter (kept hidden, revealed on the iframe's `ready` message
  // so there's no loader flash), and TEAR DOWN on exit (drop the iframe → release
  // the 2nd WebGL context). handleCloud is a hoisted declaration so the world's
  // onLevelChange (set above) can call it; `world`/`hud` are referenced only from
  // handlers that fire after boot, so the forward refs are safe.
  const PUB = process.env.PUBLIC_URL || '';
  const CLOUD_SRC = PUB + '/cloud/cloud.html';
  const cloudPortal = document.getElementById('cloud-portal');
  const cloudFrame = document.getElementById('cp-iframe');
  const cpExit = document.getElementById('cp-exit');
  let cloudBuilt = false, cloudReady = false, cloudWantOpen = false, cloudBeatDone = false;
  let cloudBeatT = null, cloudFallbackT = null, cloudTeardownT = null, cloudIdle = null;
  function cloudMsg(action) {
    try { cloudFrame && cloudFrame.contentWindow && cloudFrame.contentWindow.postMessage({ type: 'serga-cloud', action }, window.location.origin); } catch (e) {}
  }
  function prefetchCloud() {
    if (cloudBuilt || !cloudFrame || document.getElementById('cloud-prefetch')) return;
    const wrap = document.createElement('div'); wrap.id = 'cloud-prefetch'; wrap.style.display = 'none';
    ['/cloud/cloud.html', '/cloud/cloud.js', '/cloud/cloud-media.html', '/cloud/cloud-files.html',
     '/cloud/media-ui.js', '/cloud/media-globe.js',
     '/cloud/media-data.js', '/cloud/cloud-api.js', '/cloud/cloud-auth.js', '/cloud/files.js', '/cloud/vendor/three.module.js',
     '/cloud/assets/station.glb', '/cloud/assets/earth-blue-marble.jpg'].forEach(p => {
      const l = document.createElement('link'); l.rel = 'prefetch'; l.href = PUB + p; wrap.appendChild(l);
    });
    document.head.appendChild(wrap);
  }
  function revealCloud() {
    if (!cloudWantOpen || !cloudBeatDone || !cloudReady || !cloudPortal) return;
    clearTimeout(cloudFallbackT);
    if (!cloudPortal.classList.contains('open')) {
      cloudPortal.classList.add('open');
      document.body.classList.add('cloud-open');  // lift the CONFIG/LOCK/SHELL cluster above the portal
      cloudMsg('flyin');
    }
  }
  function enterCloud() {
    if (!cloudPortal || !cloudFrame) return;
    cloudWantOpen = true; cloudBeatDone = false; clearTimeout(cloudTeardownT);
    // Lay the iframe in only while it has content. When idle it stays
    // display:none — otherwise Safari/WebKit routes wheel + pinch into the
    // (scaled, full-screen) iframe even though it's pointer-events:none, killing
    // scroll/zoom on the OS field everywhere except the ~15px edge it doesn't cover.
    cloudFrame.style.display = 'block';
    if (!cloudBuilt) { cloudReady = false; cloudFrame.setAttribute('src', CLOUD_SRC); cloudBuilt = true; }
    cloudMsg('wake');
    // hold a beat so the OS field's dive toward the node is visible, then reveal
    clearTimeout(cloudBeatT);
    cloudBeatT = setTimeout(() => { cloudBeatDone = true; revealCloud(); }, 380);
    // safety: reveal even if `ready` never arrives (slow build / offline node)
    clearTimeout(cloudFallbackT);
    cloudFallbackT = setTimeout(() => { cloudReady = true; revealCloud(); }, 4000);
  }
  function exitCloud() {
    if (!cloudPortal) return;
    cloudWantOpen = false; cloudBeatDone = false;
    clearTimeout(cloudFallbackT); clearTimeout(cloudBeatT);
    const wasOpen = cloudPortal.classList.contains('open');
    cloudPortal.classList.remove('open');
    document.body.classList.remove('cloud-open');
    if (!cloudBuilt) return;
    // drop the iframe to free the 2nd WebGL context + memory — also when we exit
    // BEFORE the portal ever revealed (build started, never opened), else it leaks.
    clearTimeout(cloudTeardownT);
    cloudTeardownT = setTimeout(() => {
      if (!cloudWantOpen && cloudFrame) {
        cloudFrame.removeAttribute('src'); cloudBuilt = false; cloudReady = false;
        cloudFrame.style.display = 'none'; // remove from hit-testing while idle (see enterCloud)
      }
    }, wasOpen ? 900 : 0);
  }
  function handleCloud(info) {
    const onCloud = info && info.level === 1 && info.zone && info.zone.data && info.zone.data.id === 'cloud';
    if (onCloud) enterCloud(); else exitCloud();
  }
  function onCloudMessage(e) {
    const d = e.data || {};
    if (!d || d.type !== 'serga-cloud') return;
    if (!cloudFrame || e.source !== cloudFrame.contentWindow) return; // only our portal frame
    if (d.action === 'ready') { cloudReady = true; revealCloud(); }
    else if (d.action === 'exit') { world.back(); }
    else if (d.action === 'request-token') {
      // the CLOUD bundle wants the operator's Cognito idToken to link the home
      // node (silent SSO → lokale_session cookie). Hand it back into the iframe.
      let tk = null;
      try { tk = opts.getCloudToken && opts.getCloudToken(); } catch (e2) {}
      try { cloudFrame.contentWindow && cloudFrame.contentWindow.postMessage({ type: 'serga-cloud', action: 'token', idToken: tk || null }, window.location.origin); } catch (e2) {}
    }
  }
  addEventListener('message', onCloudMessage);
  const onCpExit = () => world.back();
  if (cpExit) cpExit.addEventListener('click', onCpExit);
  // warm the bundle bytes once the OS settles (no live scene built yet)
  if ('requestIdleCallback' in window) cloudIdle = requestIdleCallback(prefetchCloud, { timeout: 3500 });
  else cloudIdle = setTimeout(prefetchCloud, 2600);

  let hud;
  const world = createWorld({
    canvas: document.getElementById('scene'),
    cardsLayer: document.getElementById('css3d'),
    settings,
    onLevelChange: info => { hud && hud.updateNav(info); handleCloud(info); },
  });

  hud = createHUD(world, settings, (key, val) => {
    if (key === 'accent') { applyAccentCSS(val); world.setSettings({ accent: val }); }
    else if (key === 'scan') { applyScan(val); }
    else { world.setSettings({ [key]: val }); }
  });

  // ── FEED tray — agent-curated FOCUS + Mastodon stream (right rail) ──────────
  const tray = createTray(world);

  // ── SONIC tray — Spotify transport (right dock, mutually exclusive w/ FEED) ──
  const sonic = createSonic();
  window.SONIC = sonic;

  // ── SERGA SHELL — content editor console ──────────────────────────────────
  const shell = createShell(world, () => hud.buildDash(), opts.reloadProjects, tray, sonic);
  const shellBtn = document.getElementById('shellbtn');
  const onShellClick = () => shell.toggle();
  if (shellBtn) shellBtn.addEventListener('click', onShellClick);
  const onBacktick = e => {
    if (document.body.classList.contains('locked')) return;
    if ((e.key === '`' || e.key === '~') && e.target.id !== 'sh-line') { e.preventDefault(); shell.toggle(); }
    if ((e.key === 'f' || e.key === 'F') && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') { e.preventDefault(); toggleFullscreen(); }
  };
  addEventListener('keydown', onBacktick);
  window.SHELL = shell;

  // ── FULLSCREEN toggle — flips the whole page in/out of native fullscreen ─────
  const fsBtn = document.getElementById('fsbtn');
  function inFullscreen() { return !!(document.fullscreenElement || document.webkitFullscreenElement); }
  function toggleFullscreen() {
    try {
      if (inFullscreen()) { (document.exitFullscreen || document.webkitExitFullscreen).call(document); }
      else { const el = document.documentElement; (el.requestFullscreen || el.webkitRequestFullscreen).call(el); }
    } catch (e) {}
  }
  function syncFsBtn() {
    if (!fsBtn) return;
    const on = inFullscreen();
    fsBtn.classList.toggle('on', on);
    fsBtn.title = on ? 'exit fullscreen (F)' : 'fullscreen (F)';
  }
  const onFsClick = () => toggleFullscreen();
  if (fsBtn) fsBtn.addEventListener('click', onFsClick);
  document.addEventListener('fullscreenchange', syncFsBtn);
  document.addEventListener('webkitfullscreenchange', syncFsBtn);
  syncFsBtn();

  // start locked: the constellation stays collapsed behind the auth card until
  // the React auth gate (SergaOS.tsx, wired to Cognito) calls world.unlock().
  document.body.classList.add('locked');
  world.lock();

  // ── boot sequence ──────────────────────────────────────────────────────────
  const bootEl = document.getElementById('boot');
  const bootLog = bootEl.querySelector('.bootlog');
  const bootBar = bootEl.querySelector('.bootbar i');
  const lines = authed ? [
    'INITIALISING KERNEL…',
    'MOUNTING AREAS … PROJECTS · FINANCES · TRAVEL',
    'LINKING ' + projectCount + ' ACTIVE PROJECTS',
    'RENDERING SPATIAL FIELD',
    'SESSION RESTORED — ' + sessionLabel,
  ] : [
    'INITIALISING KERNEL…',
    'MOUNTING SECURE ENCLAVE',
    'ESTABLISHING ENCRYPTED CHANNEL',
    'RENDERING SPATIAL FIELD',
    'AWAITING OPERATOR CREDENTIALS',
  ];
  let li = 0;
  const timers = [];
  bootEl.classList.remove('gone');
  const bootInt = setInterval(() => {
    bootLog.textContent = lines[li] || '';
    bootBar.style.width = ((li + 1) / lines.length * 100) + '%';
    li++;
    if (li > lines.length) {
      clearInterval(bootInt);
      timers.push(setTimeout(() => bootEl.classList.add('gone'), 420));
    }
  }, 340);

  // the auth gate drives the first reveal: world.unlock() blooms the overview
  // once Cognito reports an authenticated session.
  window.SERGA = world;

  // safety net: guarantee boot clears even if timers were throttled in background
  timers.push(setTimeout(() => bootEl.classList.add('gone'), 4200));
  const clearBoot = () => bootEl.classList.add('gone');
  addEventListener('pointerdown', clearBoot, { once: true });

  // ── teardown ────────────────────────────────────────────────────────────────
  function cleanup() {
    clearInterval(bootInt);
    timers.forEach(clearTimeout);
    removeEventListener('pointerdown', clearBoot);
    removeEventListener('keydown', onBacktick);
    // CLOUD portal teardown
    clearTimeout(cloudBeatT); clearTimeout(cloudFallbackT); clearTimeout(cloudTeardownT);
    if (cloudIdle != null) { if ('cancelIdleCallback' in window) { try { cancelIdleCallback(cloudIdle); } catch (e) {} } else { clearTimeout(cloudIdle); } }
    removeEventListener('message', onCloudMessage);
    if (cpExit) cpExit.removeEventListener('click', onCpExit);
    if (cloudFrame) cloudFrame.removeAttribute('src');
    { const pf = document.getElementById('cloud-prefetch'); if (pf) pf.remove(); }
    if (shellBtn) shellBtn.removeEventListener('click', onShellClick);
    if (fsBtn) fsBtn.removeEventListener('click', onFsClick);
    document.removeEventListener('fullscreenchange', syncFsBtn);
    document.removeEventListener('webkitfullscreenchange', syncFsBtn);
    document.body.classList.remove('locked', 'cloud-open');
    shell.dispose();
    tray.dispose();
    sonic.dispose();
    hud.dispose();
    world.dispose();
    if (window.SHELL === shell) delete window.SHELL;
    if (window.SONIC === sonic) delete window.SONIC;
    if (window.SERGA === world) delete window.SERGA;
  }
  return { cleanup, world, tray, sonic };
}
